/**
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { IFilterData } from 'redux/filterDataType';
import { IMachine, IPositionFair, ISoftware } from '@umati/dashboard-types';

export type FilterFunction<FilteredType> = (machines: FilteredType[], ...props: any[]) => FilteredType[];
export type DataPropertyType<FilterDataType> = keyof FilterDataType;

export class FilterSpec<FilteredType, FilterDataType> {
  readonly Name: string;
  readonly Filter: FilterFunction<FilteredType>;
  readonly Keys: DataPropertyType<FilterDataType>[];
  constructor(name: string, filter: FilterFunction<FilteredType>, ...keys: DataPropertyType<FilterDataType>[]) {
    this.Name = name;
    this.Filter = filter;
    this.Keys = keys;
  }
}

export type SimpleFilterFunction<FilteredType> = (machines: FilteredType, ...props: any[]) => boolean;

export class SimpleFilter<FilteredType, FilterDataType> extends FilterSpec<FilteredType, FilterDataType> {
  constructor(
    name: string,
    simpleFilter: SimpleFilterFunction<FilteredType>,
    ...keys: DataPropertyType<FilterDataType>[]
  ) {
    super(name, (machines: FilteredType[], ...prop) => machines.filter((m) => simpleFilter(m, ...prop)), ...keys);
  }
}

type ListElementType = IMachine | ISoftware;
type ListElementFilterDataType = IFilterData;

export function FilterFair<T extends ListElementType, D extends ListElementFilterDataType>() {
  return new SimpleFilter<T, D>(
    'Fair',
    (m: T, fairTag?: string) => {
      if (!fairTag || fairTag === 'all') {
        return true;
      }
      return !!m.Positions && fairTag in m.Positions;
    },
    'fairTag',
  );
}

export function FilterHalls<T extends ListElementType, D extends ListElementFilterDataType>() {
  return new SimpleFilter<T, D>(
    'Halls',
    (m: T, fairTag?: string, halls?: string) => {
      if (halls === null || halls === undefined || halls === 'all') {
        return true;
      }
      if (!fairTag) {
        // Only possible with fair
        return false;
      }
      const position: IPositionFair | undefined = m.Positions?.[fairTag] as IPositionFair;
      const machineHall = position?.hall;
      return halls === machineHall;
    },
    'fairTag',
    'halls',
  );
}

export function FilterFavourite<T extends ListElementType, D extends ListElementFilterDataType>() {
  return new SimpleFilter<T, D>(
    'Favourite',
    (m: T, showFavourites?: boolean) => {
      if (!showFavourites) {
        return true;
      }
      return !!m.Favorite;
    },
    'showFavourites',
  );
}

export class Filter<T extends ListElementType, D extends ListElementFilterDataType> {
  filterData: D;
  filters: FilterSpec<T, D>[];

  constructor(filterData: D, filters: FilterSpec<T, D>[]) {
    this.filterData = filterData;
    this.filters = filters;
  }

  filter(elements: T[]): T[] {
    let filteredElements = elements;
    for (const filter of this.filters) {
      const pars: any[] = [];
      for (const parKey of filter.Keys) {
        pars.push(this.filterData[parKey]);
      }
      // const prevLen = filteredElements.length;
      filteredElements = filter.Filter(filteredElements, ...pars);
      const postLen = filteredElements.length;
      // console.log(`Filtered ${filter.Name}: ${prevLen} -> ${postLen}`);
      if (postLen === 0) {
        break;
      }
    }
    return filteredElements;
  }
}
